/* eslint-disable react/no-unused-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Body1,
  Caption1,
  Card,
  Spinner,
  Subtitle2,
  Tab,
  TabList,
  Text,
  Title3,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  ArrowTrendingRegular,
  DataBarVertical20Regular,
  DataLine20Regular,
  DataTrendingRegular,
  MoneyRegular,
} from '@fluentui/react-icons';

import { AppState } from '../../../redux/reducers';
import * as CashFlowStatisticsService from '../../../services/CashFlow-Statistics.Services';
import {
  CashflowSeriesPointViewModel,
  MonthlyCashflowStatisticViewModel,
  StatisticsFilter,
  StatisticsTimeSeriesFilter,
} from '../../../models/Entities/CashFlow/Statistics/StatisticsModels';
import ProyectionDataGridComponent from './ProyectionDataGrid.Component';
import { chartTooltipColors, useProtocolChartPalette, withAlpha } from '../ProtocolChartTheme';

/** Public props — backwards compatible: legacy fields are accepted and ignored. */
export interface ProyectionChartProps {
  // ---- New (real-data) props ----
  protocolId?: number;
  /** When provided (length >= 1), overrides protocolId and aggregates results from each id. */
  protocolIds?: number[];
  sponsorId?: number;
  startDate?: string;
  endDate?: string;
  currencyCode?: string;
  /** Threshold (%) painted as min-margin reference line. */
  minMarginThreshold?: number;

  // ---- Visual / label overrides (still supported) ----
  currencySymbol?: string;
  titleBars?: string;
  titleFlow?: string;
  costLabel?: string;
  revenueLabel?: string;
  netLabel?: string;
  cumulativeLabel?: string;
  marginLabel?: string;
  minMarginLabel?: string;
  providerCostLabel?: string;
  medicalTeamCostLabel?: string;

  // ---- Legacy props (accepted but no longer used; kept for back-compat) ----
  revenueData?: any;
  costData?: any;
  showMonthlyNetLine?: boolean;
  costPercentageFallback?: number;
  costVariancePct?: number;
  revenueIsQuarterly?: boolean;
  revenueMonthIndexInQuarter?: 0 | 1 | 2;
  showNetBar?: boolean;
  showMarginLine?: boolean;
  validateConsistency?: boolean;
  consistencyTolerance?: number;
  showDiscrepancyMarkers?: boolean;
  showIncomeMarkers?: boolean;
  showRevenueBarLabels?: boolean;
  showMinMarginLine?: boolean;
  actualMonthsEndIndex?: number;
  providerCostShare?: number;
  providerCostsOverride?: number[];
  medicalTeamCostsOverride?: number[];
  actualRevenueColor?: string;
  projectedRevenueColor?: string;
  actualCostColor?: string;
  projectedCostColor?: string;
  actualMedicalTeamCostColor?: string;
  projectedMedicalTeamCostColor?: string;
  actualCumulativeColor?: string;
  projectedCumulativeColor?: string;
  actualNetColor?: string;
  projectedNetColor?: string;
}

interface ConnectedProps {
  AccessToken: string | undefined;
}

type Props = ProyectionChartProps & ConnectedProps;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    columnGap: '16px',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: '8px',
    columnGap: '8px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column' },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    rowGap: '12px',
    columnGap: '12px',
  },
  kpiCard: {
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    rowGap: '6px',
    columnGap: '6px',
    position: 'relative',
    overflow: 'hidden',
  },
  kpiHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    color: tokens.colorNeutralForeground3,
  },
  kpiValue: {
    fontSize: '24px',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  kpiAccent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',
  },
  accentBlue: { backgroundColor: tokens.colorBrandBackground },
  accentRed: { backgroundColor: tokens.colorPaletteRedBackground3 },
  accentGreen: { backgroundColor: tokens.colorPaletteGreenBackground3 },
  accentAmber: { backgroundColor: tokens.colorPaletteMarigoldBackground3 },
  positive: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: 600,
  },
  negative: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 600,
  },
  chartCard: {
    ...shorthands.padding('12px'),
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
  },
  emptyState: {
    ...shorthands.padding('32px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  centerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('24px'),
  },
});

const todayIso = () => new Date().toISOString().slice(0, 10);
const oneYearBackIso = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

// Aggregate monthly rows from multiple protocols into one row per (year, month, currency).
const aggregateMonthly = (
  rows: MonthlyCashflowStatisticViewModel[]
): MonthlyCashflowStatisticViewModel[] => {
  const map = new Map<string, MonthlyCashflowStatisticViewModel>();
  rows.forEach((r) => {
    const key = `${r.year}|${r.month}|${r.currencyCode}`;
    const cur = map.get(key);
    if (!cur) {
      map.set(key, { ...r });
      return;
    }
    cur.income = (cur.income || 0) + (r.income || 0);
    cur.outcome = (cur.outcome || 0) + (r.outcome || 0);
    cur.net = (cur.net || 0) + (r.net || 0);
    cur.incomeCount = (cur.incomeCount || 0) + (r.incomeCount || 0);
    cur.outcomeCount = (cur.outcomeCount || 0) + (r.outcomeCount || 0);
  });
  return Array.from(map.values());
};

// Aggregate cashflow series points from multiple protocols into one point per (periodStart, currency).
// cumulativeNet is recomputed after summing.
const aggregateSeries = (
  points: CashflowSeriesPointViewModel[]
): CashflowSeriesPointViewModel[] => {
  const map = new Map<string, CashflowSeriesPointViewModel>();
  points.forEach((p) => {
    const key = `${p.periodStart}|${p.currencyCode}`;
    const cur = map.get(key);
    if (!cur) {
      map.set(key, { ...p });
      return;
    }
    cur.income = (cur.income || 0) + (p.income || 0);
    cur.outcome = (cur.outcome || 0) + (p.outcome || 0);
    cur.net = (cur.net || 0) + (p.net || 0);
    cur.incomeCount = (cur.incomeCount || 0) + (p.incomeCount || 0);
    cur.outcomeCount = (cur.outcomeCount || 0) + (p.outcomeCount || 0);
  });
  const all = Array.from(map.values()).sort((a, b) => {
    if (a.periodStart < b.periodStart) return -1;
    if (a.periodStart > b.periodStart) return 1;
    return 0;
  });
  // Recompute cumulativeNet per currency in chronological order.
  const runningByCurrency = new Map<string, number>();
  all.forEach((p) => {
    const acc = (runningByCurrency.get(p.currencyCode) || 0) + (p.net || 0);
    runningByCurrency.set(p.currencyCode, acc);
    p.cumulativeNet = acc;
  });
  return all;
};

const renderProtocolCaption = (ids: number[], t: (key: string) => string): string => {
  if (ids.length === 0) return '';
  if (ids.length === 1) return ` · protocolId=${ids[0]}`;
  return ` · ${ids.length} ${t('protocols') || 'protocolos'}`;
};

const ProyectionComponent: React.FC<Props> = (props) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const palette = useProtocolChartPalette();

  const {
    AccessToken,
    protocolId,
    protocolIds,
    sponsorId,
    startDate,
    endDate,
    currencyCode,
    currencySymbol = '$',
    titleBars,
    titleFlow,
    costLabel,
    revenueLabel,
    netLabel,
    cumulativeLabel,
    marginLabel,
    minMarginLabel,
    minMarginThreshold = 35,
  } = props;

  // ---------- Labels ----------
  const titleBarsText = titleBars || t('incomeVsCosts');
  const titleFlowText = titleFlow || t('cashFlow');
  const costLabelText = costLabel || t('costs');
  const revenueLabelText = revenueLabel || t('income');
  const netLabelText = netLabel || t('monthlyNet');
  const cumulativeLabelText = cumulativeLabel || t('cumulativeBalance');
  const marginLabelText = marginLabel || t('marginPercent');
  const minMarginLabelText = minMarginLabel || t('minimumProfitability');

  // ---------- Filter ----------
  // Resolve the effective protocol id list (memoized via JSON key to avoid array identity churn).
  const protocolIdsKey = useMemo(() => {
    let list: number[];
    if (protocolIds && protocolIds.length > 0) {
      list = protocolIds;
    } else if (protocolId === undefined) {
      list = [];
    } else {
      list = [protocolId];
    }
    return Array.from(new Set(list)).sort((a, b) => a - b).join(',');
  }, [protocolIds, protocolId]);

  const effectiveProtocolIds = useMemo(
    () => (protocolIdsKey ? protocolIdsKey.split(',').map(Number) : []),
    [protocolIdsKey]
  );

  const resolvedStart = startDate || oneYearBackIso();
  const resolvedEnd = endDate || todayIso();

  const baseFilter: StatisticsFilter = useMemo(
    () => ({
      startDate: resolvedStart,
      endDate: resolvedEnd,
      protocolId: effectiveProtocolIds.length === 1 ? effectiveProtocolIds[0] : undefined,
      sponsorId,
      currencyCode,
    }),
    [resolvedStart, resolvedEnd, effectiveProtocolIds, sponsorId, currencyCode]
  );

  const seriesFilter: StatisticsTimeSeriesFilter = useMemo(
    () => ({ ...baseFilter, granularity: 'Month' }),
    [baseFilter]
  );

  // ---------- Data state ----------
  const [monthly, setMonthly] = useState<MonthlyCashflowStatisticViewModel[]>([]);
  const [series, setSeries] = useState<CashflowSeriesPointViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [selectedTab, setSelectedTab] = useState<string>('Flux');
  const [activeCurrency, setActiveCurrency] = useState<string | undefined>(undefined);

  // ---------- Load ----------
  useEffect(() => {
    if (!AccessToken) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(undefined);

    // When multiple protocols are selected, fan out per-id calls and aggregate client-side.
    const ids = effectiveProtocolIds;
    const fetchMonthlyForId = (id: number) =>
      CashFlowStatisticsService.GetMonthlyCashflow(AccessToken, { ...baseFilter, protocolId: id })
        .catch(() => [] as MonthlyCashflowStatisticViewModel[]);
    const fetchSeriesForId = (id: number) =>
      CashFlowStatisticsService.GetCashflowSeries(AccessToken, { ...seriesFilter, protocolId: id })
        .catch(() => [] as CashflowSeriesPointViewModel[]);

    const fetchAll = async () => {
      if (ids.length > 1) {
        const monthlyResults = await Promise.all(ids.map(fetchMonthlyForId));
        const seriesResults = await Promise.all(ids.map(fetchSeriesForId));
        return {
          mc: aggregateMonthly(monthlyResults.flat()),
          cs: aggregateSeries(seriesResults.flat()),
        };
      }
      const [mc, cs] = await Promise.all([
        CashFlowStatisticsService.GetMonthlyCashflow(AccessToken, baseFilter).catch(() => []),
        CashFlowStatisticsService.GetCashflowSeries(AccessToken, seriesFilter).catch(() => []),
      ]);
      return { mc, cs };
    };

    fetchAll()
      .then(({ mc, cs }) => {
        if (cancelled) return;
        setMonthly(mc);
        setSeries(cs);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [AccessToken, baseFilter, seriesFilter, effectiveProtocolIds]);

  // ---------- Currency ----------
  const availableCurrencies = useMemo(() => {
    const s = new Set<string>();
    monthly.forEach((m) => s.add(m.currencyCode));
    series.forEach((p) => s.add(p.currencyCode));
    return Array.from(s);
  }, [monthly, series]);

  useEffect(() => {
    if (availableCurrencies.length === 0) return;
    if (!activeCurrency || !availableCurrencies.includes(activeCurrency)) {
      setActiveCurrency(availableCurrencies[0]);
    }
  }, [availableCurrencies, activeCurrency]);

  const currency = activeCurrency || currencyCode || 'USD';

  const monthlyForCurrency = useMemo(
    () => monthly.filter((m) => m.currencyCode === currency),
    [monthly, currency]
  );
  const seriesForCurrency = useMemo(
    () => series.filter((p) => p.currencyCode === currency),
    [series, currency]
  );

  // ---------- Derived monthly arrays ----------
  const monthlyRows = useMemo(() => {
    // Order ascending by year/month; rely on backend to deliver one row per period (no gap fill).
    return [...monthlyForCurrency].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  }, [monthlyForCurrency]);

  const monthLabels = useMemo(
    () => monthlyRows.map((r) => `${r.year}-${String(r.month).padStart(2, '0')}`),
    [monthlyRows]
  );
  const incomeArr = useMemo(() => monthlyRows.map((r) => r.income || 0), [monthlyRows]);
  const outcomeArr = useMemo(() => monthlyRows.map((r) => r.outcome || 0), [monthlyRows]);
  const netArr = useMemo(
    () => monthlyRows.map((r) => r.net ?? ((r.income || 0) - (r.outcome || 0))),
    [monthlyRows]
  );
  const cumulativeArr = useMemo(() => {
    let acc = 0;
    return netArr.map((v) => (acc += v));
  }, [netArr]);
  const marginArr = useMemo(
    () =>
      incomeArr.map((rev, i) => {
        if (!rev) return null as number | null;
        const cost = outcomeArr[i] || 0;
        return +(((rev - cost) / rev) * 100).toFixed(2);
      }),
    [incomeArr, outcomeArr]
  );

  // ---------- KPI totals ----------
  const totalIncome = useMemo(() => incomeArr.reduce((s, v) => s + v, 0), [incomeArr]);
  const totalOutcome = useMemo(() => outcomeArr.reduce((s, v) => s + v, 0), [outcomeArr]);
  const totalNet = totalIncome - totalOutcome;
  const overallMargin = totalIncome > 0 ? (totalNet / totalIncome) * 100 : 0;

  // ---------- Helpers ----------
  const formatMoney = (v: number | null | undefined = 0) => {
    const n = v ?? 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${currencySymbol}${n.toLocaleString()}`;
    }
  };

  // ---------- Margin axis ----------
  const marginValuesAll = useMemo(() => {
    const arr = marginArr.filter((v): v is number => v !== null);
    arr.push(minMarginThreshold);
    return arr;
  }, [marginArr, minMarginThreshold]);
  const axisMarginMin = marginValuesAll.length
    ? Math.floor(Math.min(...marginValuesAll) - 5)
    : -50;
  const axisMarginMax = marginValuesAll.length
    ? Math.ceil(Math.max(...marginValuesAll) + 5)
    : 100;

  // ---------- Bars option ----------
  const barsOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      title: {
        text: titleBarsText,
        left: 'center',
        textStyle: { fontSize: 15, fontWeight: 'bold' as const, color: palette.text },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...chartTooltipColors(palette),
        formatter: (params: any[]) => {
          const lines = params.map((p: any) => {
            if (p.seriesName === marginLabelText || p.seriesName === minMarginLabelText) {
              const v = Array.isArray(p.value) ? p.value[1] : p.value;
              return `${p.marker} ${p.seriesName}: <b>${v == null ? '-' : v + '%'}</b>`;
            }
            const v = Array.isArray(p.value) ? p.value[1] : p.value;
            return `${p.marker} ${p.seriesName}: <b>${formatMoney(v)}</b>`;
          });
          return `<b>${params[0].axisValue}</b><br/>${lines.join('<br/>')}`;
        },
      },
      legend: {
        top: 28,
        data: [revenueLabelText, costLabelText, netLabelText, marginLabelText, minMarginLabelText],
        textStyle: { color: palette.textSecondary },
      },
      grid: { left: 60, right: 60, bottom: 50, top: 70 },
      xAxis: { type: 'category', data: monthLabels, axisLabel: { rotate: 20, color: palette.textSecondary }, axisLine: { lineStyle: { color: palette.axisLine } } },
      yAxis: [
        {
          type: 'value',
          axisLabel: { formatter: (val: number) => formatMoney(val), color: palette.textSecondary },
          splitLine: { lineStyle: { color: palette.gridLine } },
        },
        {
          type: 'value',
          axisLabel: { formatter: (val: number) => `${val}%`, color: palette.textSecondary },
          min: axisMarginMin,
          max: axisMarginMax,
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: revenueLabelText,
          type: 'bar',
          data: incomeArr,
          itemStyle: { color: palette.positive },
          yAxisIndex: 0,
        },
        {
          name: costLabelText,
          type: 'bar',
          data: outcomeArr.map((v) => -v),
          itemStyle: { color: palette.negative },
          yAxisIndex: 0,
          label: {
            show: false,
          },
        },
        {
          name: netLabelText,
          type: 'line',
          data: netArr,
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 2, color: palette.series[2] },
          itemStyle: { color: palette.series[2] },
          yAxisIndex: 0,
        },
        {
          name: marginLabelText,
          type: 'line',
          data: marginArr,
          smooth: true,
          yAxisIndex: 1,
          showSymbol: false,
          lineStyle: { width: 2, color: palette.series[3] },
          itemStyle: { color: palette.series[3] },
          areaStyle: { color: withAlpha(palette.series[3], 0.1) },
        },
        {
          name: minMarginLabelText,
          type: 'line',
          data: monthLabels.map(() => minMarginThreshold),
          yAxisIndex: 1,
          symbol: 'none',
          lineStyle: { width: 2, color: palette.series[4], type: 'dotted' as const },
          itemStyle: { color: palette.series[4] },
        },
      ],
    }),
    [
      titleBarsText,
      marginLabelText,
      minMarginLabelText,
      revenueLabelText,
      costLabelText,
      netLabelText,
      monthLabels,
      axisMarginMin,
      axisMarginMax,
      incomeArr,
      outcomeArr,
      netArr,
      marginArr,
      minMarginThreshold,
      currency,
      palette,
    ]
  );

  // ---------- Flow option ----------
  const flowOption = useMemo(() => {
    const labels = seriesForCurrency.length
      ? seriesForCurrency.map((p) => p.periodLabel)
      : monthLabels;
    const netSeries = seriesForCurrency.length
      ? seriesForCurrency.map((p) => p.net)
      : netArr;
    const cumSeries = seriesForCurrency.length
      ? seriesForCurrency.map((p) => p.cumulativeNet)
      : cumulativeArr;

    return {
      backgroundColor: 'transparent',
      title: {
        text: titleFlowText,
        left: 'center',
        textStyle: { fontSize: 15, fontWeight: 'bold' as const, color: palette.text },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        ...chartTooltipColors(palette),
        formatter: (params: any[]) =>
          `<b>${params[0].axisValue}</b><br/>${params
            .map((p) => `${p.marker} ${p.seriesName}: <b>${formatMoney(p.value)}</b>`)
            .join('<br/>')}`,
      },
      legend: { top: 28, data: [netLabelText, cumulativeLabelText], textStyle: { color: palette.textSecondary } },
      grid: { left: 60, right: 30, bottom: 50, top: 70 },
      xAxis: { type: 'category', data: labels, axisLabel: { rotate: 20, color: palette.textSecondary }, axisLine: { lineStyle: { color: palette.axisLine } } },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (val: number) => formatMoney(val), color: palette.textSecondary },
        splitLine: { lineStyle: { type: 'dashed' as const, color: palette.gridLine } },
      },
      series: [
        {
          name: netLabelText,
          type: 'line',
          data: netSeries,
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 2, color: palette.series[2] },
          itemStyle: { color: palette.series[2] },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: withAlpha(palette.series[2], 0.45) },
                { offset: 1, color: withAlpha(palette.series[2], 0) },
              ],
            },
          },
        },
        {
          name: cumulativeLabelText,
          type: 'line',
          data: cumSeries,
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 3, color: palette.series[3] },
          itemStyle: { color: palette.series[3] },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: withAlpha(palette.series[3], 0.55) },
                { offset: 1, color: withAlpha(palette.series[3], 0) },
              ],
            },
          },
        },
      ],
    };
  }, [
    titleFlowText,
    netLabelText,
    cumulativeLabelText,
    seriesForCurrency,
    monthLabels,
    netArr,
    cumulativeArr,
    currency,
    palette,
  ]);

  // ---------- Render ----------
  const hasData = monthlyRows.length > 0;

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Title3>{titleFlowText}</Title3>
          <Caption1>
            {baseFilter.startDate} → {baseFilter.endDate}
            {renderProtocolCaption(effectiveProtocolIds, t)}
            {sponsorId ? ` · sponsorId=${sponsorId}` : ''}
          </Caption1>
        </div>
        {availableCurrencies.length > 1 && (
          <TabList
            selectedValue={currency}
            onTabSelect={(_e, data) => setActiveCurrency(data.value as string)}
          >
            {availableCurrencies.map((c) => (
              <Tab key={c} value={c}>
                {c}
              </Tab>
            ))}
          </TabList>
        )}
      </div>

      {/* KPIs */}
      <div className={styles.kpiGrid}>
        <Card className={styles.kpiCard}>
          <div className={`${styles.kpiAccent} ${styles.accentBlue}`} />
          <div className={styles.kpiHeader}>
            <ArrowDownloadRegular />
            <Caption1>{revenueLabelText}</Caption1>
          </div>
          <Text className={styles.kpiValue}>{formatMoney(totalIncome)}</Text>
        </Card>
        <Card className={styles.kpiCard}>
          <div className={`${styles.kpiAccent} ${styles.accentRed}`} />
          <div className={styles.kpiHeader}>
            <MoneyRegular />
            <Caption1>{costLabelText}</Caption1>
          </div>
          <Text className={styles.kpiValue}>{formatMoney(totalOutcome)}</Text>
        </Card>
        <Card className={styles.kpiCard}>
          <div
            className={`${styles.kpiAccent} ${totalNet >= 0 ? styles.accentGreen : styles.accentRed}`}
          />
          <div className={styles.kpiHeader}>
            <DataTrendingRegular />
            <Caption1>{netLabelText}</Caption1>
          </div>
          <Text
            className={`${styles.kpiValue} ${totalNet >= 0 ? styles.positive : styles.negative}`}
          >
            {formatMoney(totalNet)}
          </Text>
        </Card>
        <Card className={styles.kpiCard}>
          <div
            className={`${styles.kpiAccent} ${overallMargin >= minMarginThreshold ? styles.accentGreen : styles.accentAmber}`}
          />
          <div className={styles.kpiHeader}>
            <ArrowTrendingRegular />
            <Caption1>{marginLabelText}</Caption1>
          </div>
          <Text
            className={`${styles.kpiValue} ${overallMargin >= 0 ? styles.positive : styles.negative}`}
          >
            {overallMargin.toFixed(1)}%
          </Text>
          <Caption1>
            {minMarginLabelText}: {minMarginThreshold}%
          </Caption1>
        </Card>
      </div>

      {loadError && (
        <Card className={styles.chartCard}>
          <Text className={styles.negative}>{loadError}</Text>
        </Card>
      )}

      {isLoading && (
        <div className={styles.centerRow}>
          <Spinner size="small" label={t('loading') || 'Loading'} />
        </div>
      )}

      {/* Tabs */}
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_e, data) => setSelectedTab(data.value as string)}
      >
        <Tab id="flux" icon={<DataBarVertical20Regular />} value="Flux">
          {t('incomeAndExpenses') || titleBarsText}
        </Tab>
        <Tab id="cumulative" icon={<DataLine20Regular />} value="Money">
          {t('cumulative') || cumulativeLabelText}
        </Tab>
      </TabList>

      <Card className={styles.chartCard}>
        {(() => {
          if (!hasData && !isLoading) {
            return (
              <div className={styles.emptyState}>
                <Subtitle2>{t('emptyData') || 'No data'}</Subtitle2>
                <Body1>{t('noCashflowForFilters') || 'No cashflow data for the selected filters.'}</Body1>
              </div>
            );
          }
          if (selectedTab === 'Flux') {
            return (
              <ReactECharts
                notMerge
                lazyUpdate
                option={barsOption}
                style={{ height: 420, width: '100%' }}
              />
            );
          }
          return (
            <ReactECharts
              notMerge
              lazyUpdate
              option={flowOption}
              style={{ height: 420, width: '100%' }}
            />
          );
        })()}
      </Card>

      {/* Data grid using real data */}
      {hasData && (
        <ProyectionDataGridComponent
          monthlyRevenue={incomeArr}
          monthlyCosts={outcomeArr}
          revenueLabel={revenueLabelText}
          netLabel={netLabelText}
          cumulativeLabel={cumulativeLabelText}
          marginLabel={marginLabelText}
          currencySymbol={currencySymbol}
          minMarginThreshold={minMarginThreshold}
          cumulative={cumulativeArr}
          marginPerc={marginArr}
          showCumulativeMinProfitLine={true}
          showMargin={true}
          showNet={true}
          showCostsSplit={false}
          enableExport={true}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState): ConnectedProps => ({
  AccessToken: (state as any)?.oidc?.user?.access_token as string | undefined,
});

export default connect(mapStateToProps)(ProyectionComponent) as React.FC<ProyectionChartProps>;
